import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { NoteCategories, RoleName } from '../@shared/constants/Constant';
import { IAssignRoleViewModel, IRoleViewModel } from '../model/RoleViewModel';
import {
  IAssignmentRequestResponseModel,
  IGetVendorAssignmentRequestDetailModel,
  IGetVendorSRPayLoad,
  IVendorAssignmentRequestPayLoad,
  IVendorSRViewModel,
} from '../model/Vendor.model';
import { IFinanceTransportTypeViewModel, ILoggedInUserViewModel } from '../model/common.model';
import { FileResponse, IFileUploadViewModel } from '../model/file.model';
import { GroundBaseViewModel, IRateType, IUnitType } from '../model/finance.model';
import { IRateListDetail } from '../model/rate-list.model';
import { ICommonType } from '@interfaces/common-type.interface';
import { FinanceServiceTypeInterface } from '@interfaces/finance-service-type.interface';
import { OrganizationTypeInterface } from '@interfaces/organization-type.interface';
import { BusinessUnit, BusinessUnitFilterResponse } from './business-unit';
import { AppCategoryId } from './category.model';
import { CustomerType, DriverList, VendorList } from './customer.model';
import { FilterVendorViewModel } from './filterData.model';
import { FinanceVehicleCategory } from './generateInvoiceView.model';
import { CreateBusinessUnitViewModel } from './getBusinessUnitById';
import { IGetMemberViewModel, MemberPayload, MemberViewModel, ViewMemberModel } from './memeber.model';
import { CityViewModel, ICityViewModel, StateViewModel } from './state.model';
import { BUDetailViewModel, FinanceViewModel } from './vendorDetailView.model';

// todo ror the future, services should be split into small and testable services (with clear area)
@Injectable({
  providedIn: 'root',
})
export class VendorService {
  // todo avoid using the additional next Subjects
  appCategoryIdList$: BehaviorSubject<AppCategoryId[]> = new BehaviorSubject<AppCategoryId[]>([]);
  driverList$: BehaviorSubject<DriverList[]> = new BehaviorSubject<DriverList[]>([]);
  vendorList$: BehaviorSubject<VendorList[]> = new BehaviorSubject<VendorList[]>([]);

  public memberListByBUId$: BehaviorSubject<IGetMemberViewModel[]> = new BehaviorSubject<IGetMemberViewModel[]>([]);
  public selectedVendorForDashboard: BehaviorSubject<VendorList> = new BehaviorSubject<VendorList>(null);
  private user: ILoggedInUserViewModel;
  private isCustomer: boolean;
  private isVendor: boolean;

  constructor(private httpClient: HttpClient) {
    this.user = JSON.parse(localStorage.getItem('user'));

    // todo this.isCustomer should be handle outside service
    this.isCustomer = this.user.role === RoleName.CustomerAdmin
      || this.user.role === RoleName.CustomerCoordinator;
    this.isVendor = this.user.role === RoleName.VendorAdmin ||
      this.user.role === RoleName.VendorCoordinator ||
      this.user.role === RoleName.VendorDriver;
  }

  getCityList(): Observable<CityViewModel[]> {
    const url = `${environment.apiHost}/City`;

    return this.httpClient.get<CityViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCityByState(stateId: string): Observable<ICityViewModel[]> {
    const url = `${environment.apiHost}/City/GetCityByStateId/${stateId}`;

    return this.httpClient.get<ICityViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAppCategoryData(): Observable<AppCategoryId[]> {
    const url = `${environment.apiHost}/AppCategory`;

    return this.httpClient.get<AppCategoryId[]>(url)
      .pipe(map((appCategoryIdList: AppCategoryId[]) => {
        const restrictedCategoriesForExternalRoles = [NoteCategories.pickUpInfo, NoteCategories.initialSummary,
        NoteCategories.d2D, NoteCategories.oRUpdates, NoteCategories.tripChanges, NoteCategories.mOCApproval];
        const customerCategories = [NoteCategories.catering, NoteCategories.dropOffInfo];
        if (this.isCustomer) {
          appCategoryIdList = appCategoryIdList.filter(category => customerCategories.includes(category.name));
        } else if (this.isVendor) {
          appCategoryIdList = appCategoryIdList.filter(category => !restrictedCategoriesForExternalRoles.includes(category.name));
        }

        // todo avoid using the additional Subject appCategoryIdList$
        this.appCategoryIdList$.next(appCategoryIdList);
        return appCategoryIdList;
      }))
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo DEPRECATED, use StateHttpService -> getList() instead
  getStateList(): Observable<StateViewModel[]> {
    const url = `${environment.apiHost}/State`;

    return this.httpClient.get<StateViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCustomerType(): Observable<CustomerType[]> {
    const url = `${environment.apiHost}/CustomerBU/customerType`;

    return this.httpClient.get<CustomerType[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  createVendor(vendorData: CreateBusinessUnitViewModel): Observable<string> {
    const url = `${environment.apiHost}/BusinessUnit`;

    return this.httpClient.post<string>(url, vendorData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo need to avoid using type any
  getFilterData(filterData: FilterVendorViewModel): Observable<BusinessUnitFilterResponse> {
    const url = `${environment.apiHost}/BusinessUnit/filter`;

    return this.httpClient.post<BusinessUnitFilterResponse>(url, filterData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getBusinessUnitDetailById(vendorId: string): Observable<BUDetailViewModel> {
    const url = `${environment.apiHost}/BusinessUnit/${vendorId}`;

    return this.httpClient.get<BUDetailViewModel>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getVendorList(flag: boolean): Observable<BusinessUnit[]> {
    const url = `${environment.apiHost}/BusinessUnit/${flag}`;

    return this.httpClient.get<BusinessUnit[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilteredVendorsList(): Observable<VendorList[]> {
    const url = `${environment.apiUrl}/Vendor/GetActiveVendorList`;

    return this.httpClient.get<VendorList[]>(url)
      .pipe(
        tap((vendorList: VendorList[]) => {
          // todo avoid using the additional Subject vendorList$
          this.vendorList$.next(vendorList);
        }),
        map((response: VendorList[]) => {
          return response;
        }),
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilteredDriverList(): Observable<DriverList[]> {
    const url = `${environment.apiUrl}/ServiceRequest/FilteredMemberList`;

    return this.httpClient.get<DriverList[]>(url)
      .pipe(
        tap((driverList: DriverList[]) => {
          // todo avoid using the additional Subject driverList$
          this.driverList$.next(driverList);
        }),
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateVendorDetail(vendorId: string, vendorData: CreateBusinessUnitViewModel): Observable<boolean> {
    const url = `${environment.apiHost}/BusinessUnit/${vendorId}`;

    return this.httpClient.put<boolean>(url, vendorData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo need to avoid using type any
  getVendorDetailEnable(isEnable: boolean, businessUnitId: string): Observable<boolean> {
    const url = `${environment.apiHost}/BusinessUnit/${isEnable}/${businessUnitId}`;

    return this.httpClient.get<boolean>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getMemberList(flag: boolean, buId: string): Observable<ViewMemberModel[]> {
    const url = `${environment.apiHost}/Member/${buId}/${flag}`;

    return this.httpClient.get<ViewMemberModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo need to discuss the response model with the BE developers; it is different.
  getMemberListByCustomerID(buId: string): Observable<IGetMemberViewModel[]> {
    const url = `${environment.apiHost}/Member/memberList/${buId}`;

    return this.httpClient.get<IGetMemberViewModel[]>(url)
      .pipe(
        map((response: IGetMemberViewModel[]) => {
          // todo avoid using the additional Subject memberListByBUId$
          this.memberListByBUId$.next(response);
          return response;
        }),
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo we can remove param buId
  createMember(memberDetail: MemberPayload, buId: string): Observable<string> {
    const url = `${environment.apiHost}/Member`;

    return this.httpClient.post<string>(url, memberDetail)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateMemberDetail(memberDetail: MemberPayload, memberId: string): Observable<boolean> {
    const url = `${environment.apiHost}/Member/${memberId}`;

    return this.httpClient.put<boolean>(url, memberDetail)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  toggleMemberDetail(memberId: string, buId: string, flag: boolean): Observable<unknown> {
    const url = `${environment.apiHost}/Member/${memberId}/${buId}/${flag}`;

    return this.httpClient.put(url, {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  deleteMemberDetail(memberId: string, buId: string): Observable<boolean> {
    const url = `${environment.apiHost}/Member/${memberId}/${buId}`;

    return this.httpClient.get<boolean>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllRole(roleType: string): Observable<IRoleViewModel[]> {
    const url = `${environment.apiHost}/Role/roleList/${roleType}`;

    return this.httpClient.get<IRoleViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllOrganization(): Observable<OrganizationTypeInterface[]> {
    const url = `${environment.apiHost}/AppCategory/organizationType`;

    return this.httpClient.get<OrganizationTypeInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCustomerNoteCategory(): Observable<AppCategoryId[]> {
    const url = `${environment.apiHost}/AppCategory/customerNoteCategory`;

    return this.httpClient.get<AppCategoryId[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllMemberList(id: string): Observable<ViewMemberModel[]> {
    const url = `${environment.apiHost}/Member/customerMember/${id}`;

    return this.httpClient.get<ViewMemberModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addExistingMember(memberDetail: MemberPayload, id: string): Observable<boolean> {
    const url = `${environment.apiHost}/Member/existingMember/${id}`;

    return this.httpClient.post<boolean>(url, memberDetail)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  //TODO: REMOVE
  getFileDownload(category, id: string): Observable<IFileUploadViewModel[]> {
    const url = `${environment.apiHost}/Files/downloadFile/${category}/${id}`;

    return this.httpClient.get<IFileUploadViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  //TODO: REMOVE
  deletedFile(file: any): Observable<FileResponse[]> {
    const url = `${environment.apiHost}/Files/deleteFile?${file}`;

    return this.httpClient.delete<FileResponse[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  //TODO: REMOVE
  // todo need to avoid using type any
  editFile(fileList: any): Observable<string> {
    const url = `${environment.apiHost}/Files/editFile`;

    return this.httpClient.post<string>(url, fileList)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAirCategory(): Observable<FinanceVehicleCategory[]> {
    const url = `${environment.apiHost}/AppCategory/FinanceAirCategory`;

    return this.httpClient.get<FinanceVehicleCategory[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getGroundCategory(): Observable<FinanceVehicleCategory[]> {
    const url = `${environment.apiHost}/AppCategory/financeGroundCategory`;

    return this.httpClient.get<FinanceVehicleCategory[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getRateType(): Observable<IRateType[]> {
    const url = `${environment.apiHost}/AppCategory/RateType`;

    return this.httpClient.get<IRateType[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getUnitType(): Observable<IUnitType[]> {
    const url = `${environment.apiHost}/AppCategory/UnitType`;

    return this.httpClient.get<IUnitType[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFinanceBookingCategory(): Observable<FinanceVehicleCategory[]> {
    const url = `${environment.apiHost}/AppCategory/FinanceBookingsCategory`;

    return this.httpClient.get<FinanceVehicleCategory[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFinanceServiceTypes(): Observable<FinanceServiceTypeInterface[]> {
    const url = `${environment.apiHost}/AppCategory/FinanceServiceType`;

    return this.httpClient.get<FinanceServiceTypeInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFinanceTransportTypes(): Observable<IFinanceTransportTypeViewModel[]> {
    const url = `${environment.apiHost}/AppCategory/FinanceTransportType`;

    return this.httpClient.get<IFinanceTransportTypeViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo need to avoid using type any
  saveFinanceData(financeData: any): Observable<boolean> {
    const url = `${environment.apiHost}/Finance/SaveFinanceSettings`;

    return this.httpClient.post<boolean>(url, financeData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFinanceData(id: string): Observable<FinanceViewModel> {
    const url = `${environment.apiHost}/Finance/FinanceSettingsByCustomerId/${id}`;

    return this.httpClient.get<FinanceViewModel>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo need to avoid using type any
  // todo need to discuss with backend devs about response type
  getQBCustomerList(memberId): Observable<any> {
    const url = `${environment.apiHost}/QB/GetCustomerList/${memberId}`;

    return this.httpClient.get(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo need to avoid using type any
  // todo need to discuss with backend devs about response type
  getQBClassList(memberId): Observable<any> {
    const url = `${environment.apiHost}/QB/GetClassList/${memberId}`;

    return this.httpClient.get(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  // todo need to avoid using type any
  editFinanceData(financeData: any): Observable<boolean> {
    const url = `${environment.apiHost}/Finance/UpdateFinanceSettings`;

    return this.httpClient.put<boolean>(url, financeData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  removeFinanceData(id: string): Observable<boolean> {
    const url = `${environment.apiHost}/Finance/DeleteFinanceSettings/${id}`;

    return this.httpClient.delete<boolean>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  reassignRole(reassignRolePayload: IAssignRoleViewModel): Observable<boolean> {
    const url = `${environment.apiHost}/Role/assignRole`;

    return this.httpClient.post<boolean>(url, reassignRolePayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllVendorCustomerMemberList(buId: string, isShowAll: boolean, offsetValue: number, limit: number): Observable<MemberViewModel> {
    const url = `${environment.apiHost}/Member/${buId}/${isShowAll}/${offsetValue}/${limit}`;

    return this.httpClient.get<MemberViewModel>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchMemberDetailById(memberBusinessUnitId: string): Observable<ViewMemberModel[]> {
    const url = `${environment.apiHost}/Member/FetchMemberDetail/${memberBusinessUnitId}`;

    return this.httpClient.get<ViewMemberModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchCustomerRateListByCustomerId(
    customerId: string,
    providerType: string,
    serviceRequestType: string,
    transportType: string,
    vehicleCategoryId: string): Observable<IRateListDetail> {
    const url = `${environment.apiHost}/FinanceSrGroup/GetRateListByCustomerId/${customerId}/${providerType}/${serviceRequestType}/${transportType}/${vehicleCategoryId}`;

    return this.httpClient.get<IRateListDetail>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  GetFilteredServiceRequests(payLoad: IGetVendorSRPayLoad): Observable<IVendorSRViewModel> {
    const url = `${environment.apiUrl}/Vendor/GetFilteredServiceRequests`;

    return this.httpClient.post<IVendorSRViewModel>(url, payLoad)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  sendVendorAssignmentRequest(payLoad: IVendorAssignmentRequestPayLoad): Observable<boolean> {
    const url = `${environment.apiUrl}/Vendor/SendVendorAssignmentRequest`;

    return this.httpClient.post<boolean>(url, payLoad)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAssignmentRequestList(page: number): Observable<IAssignmentRequestResponseModel> {
    const url = `${environment.apiUrl}/Vendor/GetAssignmentRequestList/${page}/10`;

    return this.httpClient.get<IAssignmentRequestResponseModel>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getVendorAssignmentRequestDetail(serviceRequestId: string): Observable<IGetVendorAssignmentRequestDetailModel[]> {
    const url = `${environment.apiUrl}/Vendor/GetVendorAssignmentRequestDetails/${serviceRequestId}`;
    return this.httpClient.get<IGetVendorAssignmentRequestDetailModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllGroundVendorList(): Observable<ICommonType[]> {
    const url = `${environment.apiUrl}/Vendor/GetAllGroundVendorList`;
    return this.httpClient.get<ICommonType[]>(url).pipe(catchError((error) => throwError(error)));
  }

  getAllAirVendorList(): Observable<ICommonType[]> {
    const url = `${environment.apiUrl}/Vendor/GetAllAirVendorList`;
    return this.httpClient.get<ICommonType[]>(url).pipe(catchError((error) => throwError(error)));
  }

  getGroundBaseList(): Observable<GroundBaseViewModel[]> {
    const url = `${environment.apiHost}/AppCategory/GetGroundBaseList`;
    return this.httpClient.get<GroundBaseViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

}
